import {PartyType} from '@sollapay/enums';
import {Button, Modal, ModalClose, Tabs, Typography} from '@sollapay/ui/components';
import {EditBuyerIcon} from '@sollapay/ui/icons';
import {useState} from 'react';
import {useTranslation} from 'react-i18next';

import {ConfirmActionModal, ErrorAlertMessage} from '@/components';
import {findByIdOrFirst, formatBuyerDisplayName} from '@/features/buyers/utils';
import {
  useCurrentTrustAccount,
  useTrustAccountPermissions
} from '@/features/trust-accounts/providers';

import {EditCorporateBuyerFields} from './EditCorporateBuyerFields';
import {EditPersonBuyerFields} from './EditPersonBuyerFields';
import {IdentityChangeApproval} from './IdentityChangeApproval';
import {useEditBuyerForm} from './useEditBuyerForm';

import type {BuyerDTO} from '@sollapay/types';
import type {FC} from 'react';

/** `onBuyerUpdated` gets the saved buyer — an ID change can re-link it to another party's id. */
export interface EditBuyerModalProps {
  trustAccountId: string;
  buyers: BuyerDTO[] | null;
  onClose: () => void;
  onBuyerUpdated?: (updatedBuyer: BuyerDTO, previousBuyerId: string) => void;
}

export const EditBuyerModal: FC<EditBuyerModalProps> = ({
  trustAccountId,
  buyers,
  onClose,
  onBuyerUpdated
}) => {
  const {t} = useTranslation(['trustAccounts', 'common']);
  const prefix = 'view.buyers.editBuyerModal';

  // Multi-buyer Save stays on the same tab (so the user can move on to the next buyer)
  // and re-disables until the next edit; single-buyer Save closes the modal outright
  // and never re-disables on dirty, so it doesn't flash before the close animation.
  const isMulti = (buyers?.length ?? 0) > 1;

  const [pendingTabId, setPendingTabId] = useState<string | null>(null);

  // Tracks only an explicit tab pick — falls back to the first buyer when nothing has
  // been picked yet, or the picked id no longer exists (e.g. after a refetch).
  const [selectedBuyerId, setSelectedBuyerId] = useState<string | null>(null);
  const activeBuyer = findByIdOrFirst(buyers, selectedBuyerId);
  const activeBuyerId = activeBuyer?.id;

  const handleSaved = (updatedBuyer: BuyerDTO) => {
    if (activeBuyerId) onBuyerUpdated?.(updatedBuyer, activeBuyerId);
    if (isMulti) setSelectedBuyerId(updatedBuyer.id);
    else onClose();
  };

  const {
    form,
    handleSave,
    resetForm,
    isSaving,
    saveError,
    isIdentityChanged,
    isIdentityChangeApproved,
    setIdentityChangeApproved
  } = useEditBuyerForm({trustAccountId, buyer: activeBuyer, onSuccess: handleSaved});

  const {isDirty} = form.formState;
  const isCorporate = activeBuyer?.partyType === PartyType.ORGANIZATION;
  const {canApproveBuyerIdentityChange} = useTrustAccountPermissions();
  const {isTrustee, lawyerName} = useCurrentTrustAccount();
  const approvalLockedTooltip = t(`${prefix}.identityApprovalLockedTooltip`);
  const isIdentityLocked = !activeBuyer?.canEditIdentity || !canApproveBuyerIdentityChange;
  const identityLockedTooltip =
    activeBuyer?.canEditIdentity && !canApproveBuyerIdentityChange
      ? approvalLockedTooltip
      : undefined;
  // An address edit needs identity approval too, but stays open after payments — so only the permission locks it.
  const addressLock = {
    isLocked: !canApproveBuyerIdentityChange,
    lockedTooltip: approvalLockedTooltip
  };
  const isAwaitingApproval = isIdentityChanged && !isIdentityChangeApproved;

  // Mounted after close, so reset on close — but never mid-save, which would drop a re-link's id swap.
  const handleOpenChange = (isOpen: boolean) => {
    if (isOpen || isSaving) return;
    resetForm();
    onClose();
  };

  const requestTabChange = (nextTabId: string) => {
    if (isSaving) return;
    if (isDirty) {
      setPendingTabId(nextTabId);
      return;
    }
    setSelectedBuyerId(nextTabId);
  };

  const confirmTabChange = () => {
    if (pendingTabId) setSelectedBuyerId(pendingTabId);
    setPendingTabId(null);
  };

  const fieldsProps = {
    form,
    trustAccountId,
    buyerId: activeBuyerId ?? '',
    isIdentityLocked,
    identityLockedTooltip,
    addressLock
  };

  return (
    <Modal
      isOpen={!!buyers?.length}
      onOpenChange={handleOpenChange}
      isDirty={isDirty}
      illustration={<EditBuyerIcon className="size-12" />}
      size="lg"
      title={
        isMulti ? (
          t(`${prefix}.multiTitle`)
        ) : (
          <Typography size="xl" weight="semibold">
            {activeBuyer?.fullName ?? ''}
          </Typography>
        )
      }
      description={t(isMulti ? `${prefix}.multiDescription` : `${prefix}.description`)}
      footer={
        <div className="flex w-full justify-end gap-3">
          <ModalClose render={<Button variant="secondary" size="sm" disabled={isSaving} />}>
            {t('common:cancel')}
          </ModalClose>
          <Button
            variant="primary"
            size="sm"
            disabled={(isMulti && !isDirty) || isAwaitingApproval}
            isLoading={isSaving}
            onClick={handleSave}
          >
            {t(`${prefix}.save`)}
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-6">
        {isMulti && buyers && (
          <Tabs
            variant="secondary"
            tabs={buyers.map(b => ({
              value: b.id,
              label: formatBuyerDisplayName(b.fullName),
              disabled: isSaving
            }))}
            value={activeBuyerId}
            onValueChange={requestTabChange}
          />
        )}
        {isCorporate ? (
          <EditCorporateBuyerFields {...fieldsProps} />
        ) : (
          <EditPersonBuyerFields {...fieldsProps} />
        )}
        {isIdentityChanged && (
          <IdentityChangeApproval
            checked={isIdentityChangeApproved}
            onCheckedChange={setIdentityChangeApproved}
            onBehalfOf={isTrustee ? undefined : (lawyerName ?? '')}
            disabled={isSaving}
          />
        )}
        {saveError != null && <ErrorAlertMessage error={saveError} />}
      </div>

      <ConfirmActionModal
        isOpen={pendingTabId !== null}
        headerAlign="start"
        title={t(`${prefix}.discardChanges.title`)}
        description={t(`${prefix}.discardChanges.description`)}
        confirmLabel={t(`${prefix}.discardChanges.confirm`)}
        cancelLabel={t(`${prefix}.discardChanges.cancel`)}
        primaryVariant="primary"
        onConfirm={confirmTabChange}
        onClose={() => setPendingTabId(null)}
      />
    </Modal>
  );
};
